import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const DISMISS_KEY = "tw_push_prompt_dismissed";

/**
 * Slim mobile-only banner that sits *above* the bottom-nav and offers the
 * user a one-tap path to enabling browser push notifications.
 *
 * Visibility gates (all must be true):
 *   1. Browser supports the Notification API
 *   2. `Notification.permission === "default"` (never asked OR denied is final)
 *   3. The user is signed in as a non-admin (b2b / b2c)
 *   4. The user has at least one saved search (otherwise notifications are
 *      useless from this entry point)
 *   5. The user has not previously dismissed the prompt in this browser
 */
export default function MobileNotifyPrompt() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [savedTotal, setSavedTotal] = useState(null);
    const [perm, setPerm] = useState(() =>
        typeof window !== "undefined" && "Notification" in window ? Notification.permission : "denied"
    );
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === "1"; }
        catch { return false; }
    });

    const isEligibleUser = user && user !== false && user.role !== "admin";

    useEffect(() => {
        // Only query the API for users that could plausibly see the banner.
        if (!isEligibleUser || perm !== "default" || dismissed) return;
        let cancelled = false;
        api.get("/saved-searches")
            .then((r) => { if (!cancelled) setSavedTotal(Array.isArray(r.data) ? r.data.length : 0); })
            .catch(() => { if (!cancelled) setSavedTotal(0); });
        return () => { cancelled = true; };
    }, [isEligibleUser, perm, dismissed]);

    const enable = async () => {
        try {
            const result = await Notification.requestPermission();
            setPerm(result);
            if (result === "granted") {
                // Subtle: do NOT dismiss-persist on grant — perm itself
                // hides the banner. If the user blocks, hide and remember.
            } else if (result === "denied") {
                try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
                setDismissed(true);
            }
        } catch (_) { /* unsupported / interrupted */ }
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
        setDismissed(true);
    };

    if (!("Notification" in window)) return null;
    if (!isEligibleUser) return null;
    if (perm !== "default") return null;
    if (dismissed) return null;
    if (savedTotal === null || savedTotal === 0) return null;

    return (
        <div
            className="md:hidden fixed bottom-16 left-2 right-2 z-30 bg-[#111827] text-white rounded-sm shadow-lg border-l-4 border-[#FFCC00] px-3 py-2.5 flex items-center gap-2"
            role="region"
            aria-label={t("notify_prompt.aria")}
            data-testid="mobile-notify-prompt"
        >
            <span className="w-9 h-9 rounded-sm bg-[#FFCC00] text-[#111827] flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0 leading-tight">
                <div className="font-bold text-xs uppercase tracking-wider truncate">
                    {t("notify_prompt.title")}
                </div>
                <div className="text-[11px] text-white/70 truncate">
                    {t("notify_prompt.body", { count: savedTotal })}
                </div>
            </div>
            <button
                type="button"
                onClick={enable}
                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm shrink-0"
                data-testid="mobile-notify-allow"
            >
                {t("notify_prompt.allow")}
            </button>
            <button
                type="button"
                onClick={dismiss}
                className="text-white/60 hover:text-white shrink-0"
                aria-label={t("notify_prompt.dismiss_aria")}
                data-testid="mobile-notify-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
